import React, { Fragment, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import { Add, AddCircle, ArrowBack, ArrowForward, CheckCircle, Delete, PersonAdd, Save, Search, Star, StarBorder, Visibility } from "@mui/icons-material";
import { Avatar, Button, Divider, InputAdornment, Menu, MenuItem, Skeleton, Tab, Tabs, TextField, Typography } from "@mui/material";
import { Box } from "@mui/system";
import { DataGridPro } from "@mui/x-data-grid-pro";
import { format, parseISO } from 'date-fns';
import _ from "underscore";

import '../styles/Views.css';

import PatientModal from "../components/PatientModal";
import { clearOrder } from "../state/actions/order";
import { addStudies, addStudy, createOrder, editStudy, fetchCustomerOrder, fetchOrder, removeStudy, updateOrder } from "../state/handlers/order";
import { selectCurrentOrder } from "../state/selectors/currentOrder";
import { setPatient } from "../state/actions/patient";
import { selectProfile } from "../state/selectors/profile";
import { selectBatchData } from "../state/selectors/batch";
import { searchStudies, selectCatalogs, selectCompleteRoutines, selectRoutines, selectStudiesByCodes } from "../state/selectors/catalogs";
import { addFavorite, removeFavorite } from "../state/handlers/catalogs";
import TechnicalNoteDialog from "../components/TechnicalNoteDialog";
import { translate } from "../helpers/genders";
import envs from "../services/EnvsService";

const ProfilesGrid = ({routines}) => {

    const dispatch = useDispatch();
    const {defaultEfectorId} = envs;
    
    const handleAdd = (studyTypes) => {
        dispatch(addStudies(studyTypes.map(s=>({studyType:{...s},efector_id : defaultEfectorId}))));
    };

    const columns = [
        {
            field: 'id',
            headerName: 'Id',
            width: 50,
            hide:true,
            sortable: false,
            type: 'number'
        },
        {
            field: 'code',
            headerName: 'Codigo',
            sortable: false,
            type: 'string',
            flex: 0.5,
            minWidth: 85
        },
        {
            field: 'name',
            headerName: 'Descripcion',
            sortable: false,
            type: 'string',
            flex: 0.5,
            minWidth: 85,
        },
        {
            field: 'add',
            headerName: 'Agregar',
            sortable: false,
            renderCell: (params) => <Button startIcon={<AddCircle style={{color:'#696969'}} onClick={()=>handleAdd(params.row.studyTypes)}/>}/>,
            
        }
    ];

    const data = {rows:routines, columns:columns}

    return(
            <DataGridPro
                style={{backgroundColor:'white'}}
                {...data}
                headerHeight={40}
                rowHeight={40}
                disableMultipleSelection
                disableColumnMenu
                disableColumnSelector
                disableMultipleColumnsSorting
                hideFooter
            />
    )

}

const StudiesGrid = ({studiesCatalog,studies}) => {


    
    const dispatch = useDispatch();
    const {defaultEfectorId} = envs;
    
    const [selected, select] = useState(undefined);

    const handleAddStudy = (studyType) => {
        dispatch(addStudy({studyType:{...studyType},efector_id: defaultEfectorId}));
    };

    const handleAddFavorite = (code) => {
        dispatch(addFavorite(code));
    }

    const handleRemoveFavorite = (code) => {
        dispatch(removeFavorite(code));
    }

    const columns = [
        {
            field: 'id',
            headerName: 'Id',
            width: 50,
            hide:true,
            sortable: false,
            type: 'number'
        },
        {
            field: 'code',
            headerName: 'Codigo',
            sortable: false,
            type: 'string',
            minWidth: 35,
        },
        {
            field: 'name',
            headerName: 'Descripcion',
            sortable: false,
            type: 'string',
            flex: 0.5,
            minWidth: 85,
        },
        {
            field: 'fav',
            headerName: 'Favorito',
            sortable: false,
            renderCell: (params) => params.row.isFavorite ? <Button onClick={()=>handleRemoveFavorite(params.row.code)} startIcon={<Star style={{color:'#696969'}}/>}/> : <Button onClick={()=>handleAddFavorite(params.row.code)} startIcon={<StarBorder style={{color:'#696969'}}/>}/>,
            
        },
        {
            field: 'view_file',
            headerName: 'Ficha',
            sortable: false,
            renderCell: (params) => <Button onClick={()=>select(params.row)} startIcon={<Visibility style={{color:'#696969'}}/>}/>,
            
        },
        {
            field: 'add',
            headerName: 'Agregar',
            sortable: false,
            renderCell: (params) => studies?.some(study => study.studyType.code === params.row.code) ? <Button startIcon={<CheckCircle style={{color:'#03716A'}} />}/> : <Button startIcon={<AddCircle style={{color:'#696969'}}  onClick={()=> handleAddStudy(params.row)} />}/>,
            
        }
    ];

    const data = {rows:studiesCatalog, columns:columns}

    return(
            <Fragment>
                <DataGridPro
                    style={{backgroundColor:'white'}}
                    {...data}
                    headerHeight={40}
                    rowHeight={40}
                    disableMultipleSelection
                    disableColumnMenu
                    disableColumnSelector
                    disableMultipleColumnsSorting
                    hideFooter
                />

                {
                    selected &&
                    <TechnicalNoteDialog open={selected} studyType={selected} onClose={()=>select(undefined)}/>
                }
            </Fragment>
    )
}

const OrderStudiesGrid = ({studies,efectors}) => {

    const {allowSelectEfector} = envs;

    const dispatch = useDispatch();

    const handleRemoveStudy = (study) => {
        dispatch(removeStudy(study));
    }

    const handleAddStudy = (study) => {
        dispatch(addStudy(study));
    };

    const handleEditStudy = (study) => {
        dispatch(editStudy(study));
    };

    const columns = [
        {
            field: 'id',
            headerName: 'Id',
            width: 50,
            hide:true,
            sortable: false,
            type: 'number'
        },
        {
            field: 'code',
            headerName: 'Codigo',
            sortable: false,
            type: 'string',
            width: 80,
            renderCell: (params) => <Typography variant="body2" style={params.row.mark_remove ? {textDecoration:'line-through'}:{}}>{params.row.studyType.code}</Typography>,
        },
        {
            field: 'name',
            headerName: 'Descripcion',
            sortable: false,
            type: 'string',
            flex: 1,
            minWidth: 50,
            renderCell: (params) => <Typography variant="body2" style={params.row.mark_remove ? {textDecoration:'line-through'}:{}}>{params.row.studyType.name}</Typography>,

        },
        {
            field: 'efector_id',
            headerName: 'Efector',
            sortable: false,
            type: 'string',
            hide:!allowSelectEfector,
            width: 160,
            
            renderCell: (params) => 
            
                <TextField
                    id="code"
                    variant="standard"
                    style={params.row.mark_remove ? {textDecoration:'line-through'}:{}}
                    select
                    value={params.row.efector_id || ''}
                    fullWidth
                    size="small"
                    inputProps={{sx:{paddingBottom:0}}}
                    InputProps={{disableUnderline: true}}
                    onChange={(event) => handleEditStudy({...params.row, efector_id : event.target.value})}
                >
                        {
                            efectors.map(
                                item => {
                                    return <MenuItem key={item.id} value={item.id}>
                                        <Typography variant="body2">{item.name}</Typography>
                                    </MenuItem>;
                                })
                        }
                </TextField>
        },
        {
            field: 'remove',
            headerName: 'Eliminar',
            sortable: false,
            renderCell: (params) => params.row.mark_remove ? <Button startIcon={<AddCircle style={{color:'#696969'}} onClick={()=>handleAddStudy(params.row)}/>}/> : <Button startIcon={<Delete style={{color:'#696969'}} onClick={()=>handleRemoveStudy(params.row)}/>}/>,
        }
    ];

    const data = {rows:studies || [] , columns:columns}

    return(
            <DataGridPro
                style={{backgroundColor:'white'}}
                {...data}
                headerHeight={40}
                rowHeight={40}
                disableMultipleSelection
                disableColumnMenu
                disableColumnSelector
                disableMultipleColumnsSorting
                hideFooter
            />
    )
}

const Order = () => {

    const [tabIndex, setTabIndex] = useState(0);
    const [patientDialog, setPatientDialog] = useState(false);
    const [filter, setFilter] = useState('');
    
    const {id} = useParams();
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const {loading, order, patient, studies, samples, measurements} = useSelector(selectCurrentOrder);
    
    const profile = useSelector(selectProfile);
    const batch = useSelector(selectBatchData);

    const studiesCatalog = useSelector(searchStudies(filter));
    const routinesCatalog = useSelector(selectCompleteRoutines());
    const {efectors} = useSelector(selectCatalogs);

    const isCustomer = profile.data?.role.code === 'customer';
    const customerId = profile.data?.customer?.id;

    useEffect(() => {
        if(id)
        {
            if(isCustomer){
                dispatch(fetchCustomerOrder(customerId,id));
            }else{
                dispatch(fetchOrder(id));
            }
        }else{
            dispatch(clearOrder);
        }
    }, []);


    useEffect(() => {
        !id && dispatch(clearOrder);
    }, [id]);

    const handleReload = () => {
        if(id)
        {
            if(isCustomer){
                dispatch(fetchCustomerOrder(customerId,id));
            }else{
                dispatch(fetchOrder(id));
            }

        }else{
            dispatch(clearOrder);
        }
    }

    const handleNewOrder = () => {
        id && navigate('/order');
        !id && dispatch(clearOrder);
    }

    const handleClosePatientDialog = (patient) => {
        patient && dispatch(setPatient(patient));
        setPatientDialog(false);
    }

    const handleContinue = () => {
        navigate(`/sample`);
    }

    const handleBack = () => {
        navigate(`/batch/${batch.id}`);
    }

    const handleFindStudy = (event) => {
        if(event.key === 'Enter' || event.keyCode === 13) {
            const studyType = studiesCatalog.find(s=>s.code === filter);
            
            if(studyType){
                dispatch(addStudy({studyType:{...studyType}}));
                setFilter('');
            }
        }
    }

    const handleSave = () => {
        !id && dispatch(createOrder(profile.data.customer.id, {...order, customer_id:profile.data.customer.id, batch_id:batch.id}, studies, samples, measurements));
        id && dispatch(updateOrder(profile.data.customer.id, {...order, customer_id:profile.data.customer.id, batch_id:batch.id},studies, samples, measurements));
    }

    
    const canSave = studies.length;

    return (
        <Fragment>
            <div className="column-container all-height">
                <div className="header row-container" style={{paddingTop:'1em', marginBottom:'1em'}}>
                    <div className="column-container " style={{paddingRight:'1em',width:'15em'}}> 
                        {
                            !order?.number &&
                            <Fragment>
                                <Skeleton/>
                                <Skeleton/>
                            </Fragment>
                        }
                        {
                            order?.number &&
                            <Fragment>
                                <Box fontWeight="bold">
                                    <Typography variant="h6" color="text.black">{`Orden N° ${order.number}`}</Typography>
                                </Box>
                                <Box fontWeight={750}>
                                    <Typography color="text.gray" variant="body2">{format(parseISO(order?.date), 'dd/MM/yyyy')}</Typography>
                                </Box>
                            </Fragment>
                        }
                    </div>

                    <Divider orientation="vertical"  sx={{ height:"5.5em"}} />

                    <div className="row-container" style={{marginLeft:'2em'}}>
                        <div className="column-container justify-center" style={{marginRight:'1em'}}>
                            <Avatar style={{height:'3em', width:'3em'}} onClick={()=>setPatientDialog(true)}>
                                <PersonAdd fontSize="large"/>
                            </Avatar>
                        </div>
                        <div className="column-container " style={{paddingRight:'1em',width:'20em'}}> 
                            
                            {
                                _.isEmpty(patient) &&
                                <Fragment>
                                    <Skeleton/>
                                    <Skeleton/>
                                    <Skeleton/>
                                    <Skeleton/>
                                </Fragment>

                            }
                            {   
                                !_.isEmpty(patient) &&                        
                                <Fragment>
                                    <Box fontWeight="medium">
                                        <Typography variant="h6" color="text.black">{`${patient.first_name} ${patient.last_name}`}</Typography>
                                    </Box>
                                    <Typography color="text.gray" variant="body2">{`N° ${patient.number}`}</Typography>
                                    <Typography color="text.gray" variant="body2">{`Fecha Nac. ${format(parseISO(patient.birth_day), 'dd/MM/yyyy')}`}</Typography>
                                    <Typography color="text.gray" variant="body2">{translate(patient.gender)}</Typography>
                                    
                                </Fragment>
                            }                    
                        </div>
                    </div>

                    <div className="grow"></div>

                    <div className="column-container align-right">
                        <Button 
                            color="primary"
                            startIcon={<ArrowBack color="secondary" />}
                            sx={{height:'2.5em', textTransform:'none', paddingLeft:'2em', paddingRight:'2em',  width:'15em', marginBottom:'1em'}}
                            onClick={()=>handleBack()}>
                                
                                <Box fontWeight="medium">
                                    <Typography>Pedido</Typography>
                                </Box>
                                
                        </Button>
                        <Button 
                            variant="outlined"
                            startIcon={<Add/>}
                            sx={{ textDecoration:'none', textTransform:"none", width:'15em'}}
                            onClick={()=>handleNewOrder()}
                        >
                            
                            <Box fontWeight="regular">
                                <Typography variant="body2">Nueva orden</Typography>
                            </Box>
                            
                        </Button>

                        
                    </div>
                </div>

                <div className="row-container body grow">
                    <div className="column-container" style={{ flexGrow:4,marginRight:'1em'}}>
                        <TextField 
                            placeholder="Buscar estudio" 
                            size="small" 
                            variant="standard" 
                            fullWidth
                            value={filter || ''}
                            onChange={(event)=>setFilter(event.target.value)}
                            onKeyUp={(event)=>handleFindStudy(event)}
                            InputProps={{
                                endAdornment:(
                                <InputAdornment position="end">
                                    <Search/>
                                </InputAdornment>)}}
                        />
                        <Tabs 
                            value={tabIndex} 
                            onChange={(event, newValue)=>setTabIndex(newValue)} 
                            variant="fullWidth" 
                            style={{marginTop:'1em', backgroundColor:'#f4f4f4'}}
                        >
                            <Tab label="Listado completo" sx={tabIndex === 0 ? {textTransform:'none', backgroundColor:'white'}:{textTransform:'none'}}/>
                            <Tab label="Favoritos" sx={tabIndex === 1 ? {textTransform:'none', backgroundColor:'white'}: {textTransform:'none'}}/>
                            <Tab label="Perfiles" sx={tabIndex === 2 ? {textTransform:'none', backgroundColor:'white'}: {textTransform:'none'}}/>
                        </Tabs>

                        <div className="grow">
                            {tabIndex===0 && <StudiesGrid studiesCatalog={studiesCatalog} studies={studies}/>}
                            {tabIndex===1 && <StudiesGrid studiesCatalog={studiesCatalog.filter(study=>study.isFavorite)} studies={studies}/>}
                            {tabIndex===2 && <ProfilesGrid routines={routinesCatalog}/>}
                        </div>
                    </div>
                    <div className="column-container" style={{flexGrow:3, paddingTop:'2.8em'}}>
                        <Tabs 
                            value={0} 
                            onChange={(event, newValue)=>setTabIndex(newValue)} 
                            variant="fullWidth"
                            style={{backgroundColor:'#f4f4f4'}}
                        >
                            <Tab label="Estudios agregados" sx={{textTransform:'none'}}/>
                        </Tabs>
                        <div className="grow">
                            <OrderStudiesGrid  studies={studies} efectors={efectors} />
                        </div>
                    </div>
                </div>

                <div className="row-container justify-right" style={{paddingRight:'2em', marginTop:'0.5em', marginBottom:'0.5em'}}>
                    <Button 
                        color="primary"
                        endIcon={<ArrowForward color="secondary" />}
                        onClick={()=>handleContinue()}
                        disabled={!order.id}
                        sx={{height:'2.5em', textTransform:'none', paddingLeft:'2em', paddingRight:'2em',  width:'15em'}}>
                            <Box fontWeight="medium">
                                <Typography>Muestras</Typography>
                            </Box>
                    </Button>
                </div>
                <div className="row-container justify-right" style={{paddingRight:'2em', marginTop:'0.5em', marginBottom:'0.5em'}}>
                    <Button 
                        color="primary"
                        variant="outlined"
                        onClick={()=>handleReload()}
                        sx={{height:'2.5em', textTransform:'none', paddingLeft:'1em', paddingRight:'1em',  width:'10em', marginBottom:'1em'}}>
                            <Box fontWeight="medium">
                                <Typography>Cancelar</Typography>
                            </Box>
                    </Button>
                   
                    <Button 
                        style={{marginLeft:'1em'}}
                        variant="contained"
                        color="secondary"
                        endIcon={<Save/>}
                        disabled={!canSave}
                        onClick={()=>handleSave()}
                        sx={{height:'2.5em', textTransform:'none', paddingLeft:'2em', paddingRight:'2em', width:'15em', marginBottom:'1em'}}>
                            
                            <Box fontWeight="medium">
                                <Typography>Guardar</Typography>
                            </Box>
                    </Button>
                </div>
            </div>

            {
                patientDialog &&
                <PatientModal open={patientDialog} onClose={handleClosePatientDialog} initPatient={{...patient}} />
            }

        </Fragment>

    )
}

export default Order;